<template>
  <div class="ayuda-wrapper pa-6">

    <div class="d-flex align-center mb-8 px-2">
      <v-icon color="#3ea6ff" class="mr-3">mdi-help-circle-outline</v-icon>
      <div>
        <h1 class="text-h5 font-weight-black white--text">Ayuda</h1>
        <div class="caption grey--text mt-1">Cómo configurar un producto en tu empresa</div>
      </div>
    </div>

    <v-expansion-panels flat multiple accordion :value="abiertos" dark>

      <!-- Bloque: El producto en tu empresa -->
      <v-expansion-panel class="ayuda-panel mb-3">
        <v-expansion-panel-header class="ayuda-header">
          <div class="d-flex align-center">
            <v-icon small color="#3ea6ff" class="mr-3">mdi-store-outline</v-icon>
            <span class="panel-title">El producto en tu empresa</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="ayuda-content">
          <p class="help-text">
            Cuando agregás un producto, estás configurando cómo ese producto existe dentro de
            tu empresa: su descripción comercial, categoría, proveedor y marca.
            Esta configuración es compartida por todas tus sucursales.
          </p>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Bloque: Presentaciones -->
      <v-expansion-panel class="ayuda-panel mb-3">
        <v-expansion-panel-header class="ayuda-header">
          <div class="d-flex align-center">
            <v-icon small color="#3ea6ff" class="mr-3">mdi-package-variant-closed</v-icon>
            <span class="panel-title">Presentaciones</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="ayuda-content">

          <div class="faq-item">
            <div class="faq-q">¿Qué es una presentación?</div>
            <p class="help-text">
              Una presentación es la unidad en la que vendés o comprás un producto.
              Por ejemplo, un agua mineral puede venderse como <em>Botella 500ml</em> o como <em>Pack x6</em>.
            </p>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Siempre tengo que crear más de una?</div>
            <p class="help-text">
              No. Si el producto se vende y se compra en una sola unidad, con una presentación alcanza.
              Creás más de una cuando:
            </p>
            <ul class="help-list">
              <li>Vendés el mismo producto en distintas unidades (unidad y caja)</li>
              <li>Comprás en una unidad y vendés en otra (kg y gramos)</li>
              <li>Ofrecés packs o presentaciones múltiples</li>
            </ul>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Qué es la presentación base?</div>
            <p class="help-text">
              Es la unidad mínima de referencia del producto. Siempre tiene que existir una.
              Las demás presentaciones se definen en relación a ella mediante el factor base.
            </p>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Qué es el factor base?</div>
            <p class="help-text">
              Es cuántas unidades base equivale una presentación. Si la base es <em>Unidad</em>
              y creás una presentación <em>Caja x12</em>, el factor base de la caja es 12.
            </p>
          </div>

          <div class="faq-q mb-3">Ejemplos</div>
          <table class="ayuda-table">
            <thead>
              <tr>
                <th>Presentación</th>
                <th>Factor base</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in ejemplosPresentaciones" :key="row.nombre">
                <td>
                  {{ row.nombre }}
                  <v-chip v-if="row.base" x-small label color="rgba(255,179,0,0.12)" text-color="#ffb300" class="ml-2 font-weight-bold">base</v-chip>
                </td>
                <td class="factor-cell">{{ row.factor }}</td>
              </tr>
            </tbody>
          </table>

        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Bloque: Tipo de stock -->
      <v-expansion-panel class="ayuda-panel mb-3">
        <v-expansion-panel-header class="ayuda-header">
          <div class="d-flex align-center">
            <v-icon small color="#3ea6ff" class="mr-3">mdi-archive-cog-outline</v-icon>
            <span class="panel-title">Tipo de stock</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="ayuda-content">

          <div class="faq-item">
            <div class="faq-q">¿Qué es el tipo de stock?</div>
            <p class="help-text">Define cómo se maneja el stock entre las presentaciones del producto.</p>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Cuáles son las opciones?</div>
            <div class="tipo-stock-bloque mb-4">
              <div class="tipo-stock-nombre">Independiente</div>
              <p class="help-text">
                Cada presentación tiene su propio stock. Usalo cuando las presentaciones son
                unidades distintas que no se convierten entre sí.
              </p>
              <div class="ejemplo-inline">
                <v-icon x-small color="grey" class="mr-1">mdi-lightbulb-outline</v-icon>
                Agua 500ml y agua 1.5L son productos distintos aunque compartan marca.
              </div>
            </div>
            <div class="tipo-stock-bloque">
              <div class="tipo-stock-nombre">Derivado (base)</div>
              <p class="help-text">
                Solo la presentación base tiene stock real. Las demás calculan automáticamente
                su stock en función del stock base y el factor.
                Usalo cuando las presentaciones son la misma unidad física en distintas cantidades.
              </p>
              <div class="ejemplo-inline">
                <v-icon x-small color="grey" class="mr-1">mdi-lightbulb-outline</v-icon>
                Si tenés 10kg en base, el sistema muestra automáticamente 20 porciones de 500g
                y 100 porciones de 100g.
              </div>
            </div>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Se puede cambiar después?</div>
            <p class="help-text">
              No. Una vez que el producto tiene movimientos de stock (ventas, compras, ajustes)
              el tipo de stock no se puede modificar.
            </p>
          </div>

        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Bloque: Sucursales y precios -->
      <v-expansion-panel class="ayuda-panel mb-3">
        <v-expansion-panel-header class="ayuda-header">
          <div class="d-flex align-center">
            <v-icon small color="#3ea6ff" class="mr-3">mdi-store-cog-outline</v-icon>
            <span class="panel-title">Sucursales y precios</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="ayuda-content">

          <div class="faq-item">
            <div class="faq-q">¿Qué significa habilitar el producto en una sucursal?</div>
            <p class="help-text">
              Un producto configurado en la empresa no está disponible automáticamente en todas
              tus sucursales. Tenés que habilitarlo en cada sucursal donde querés venderlo o
              comprarlo, y definir el precio para esa sucursal.
            </p>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Los precios son distintos por sucursal?</div>
            <p class="help-text">
              Sí. Cada sucursal puede tener su propio precio de costo y precio de venta.
              El sistema calcula el precio de venta a partir del costo y la ganancia que definís.
            </p>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Qué pasa si no habilito el producto en una sucursal?</div>
            <p class="help-text">
              El producto no aparece disponible en esa sucursal. No se puede vender ni comprar allí.
            </p>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Puedo habilitar solo algunas presentaciones en una sucursal?</div>
            <p class="help-text">
              Sí. Si el producto tiene varias presentaciones, podés habilitar solo las que usás
              en cada sucursal y fijarles precios independientes.
            </p>
          </div>

        </v-expansion-panel-content>
      </v-expansion-panel>

      <!-- Bloque: Control de stock -->
      <v-expansion-panel class="ayuda-panel mb-3">
        <v-expansion-panel-header class="ayuda-header">
          <div class="d-flex align-center">
            <v-icon small color="#3ea6ff" class="mr-3">mdi-cube-scan</v-icon>
            <span class="panel-title">Control de stock</span>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="ayuda-content">

          <div class="faq-item">
            <div class="faq-q">¿Qué significa que una presentación controle stock?</div>
            <p class="help-text">
              Significa que el sistema registra y descuenta stock automáticamente cuando se
              vende o se compra en esa presentación.
            </p>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Cuándo no activar el control de stock?</div>
            <p class="help-text">
              Cuando no necesitás llevar un conteo exacto de esa presentación. Por ejemplo,
              productos que no querés inventariar o presentaciones informativas.
            </p>
          </div>

          <div class="faq-item">
            <div class="faq-q">¿Siempre puedo elegir qué presentación controla stock?</div>
            <p class="help-text">Depende del tipo de stock:</p>
            <ul class="help-list">
              <li><strong>Independiente:</strong> cada presentación decide por separado.</li>
              <li>
                <strong>Derivado:</strong> solo la presentación base controla stock.
                Las demás lo calculan automáticamente y no pueden activar el control por separado.
              </li>
            </ul>
          </div>

        </v-expansion-panel-content>
      </v-expansion-panel>

    </v-expansion-panels>
  </div>
</template>

<script>
export default {
  name: "ProductoEmpresaAyuda",
  props: {
    productoProp: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      abiertos: [0, 1, 2, 3, 4],
      ejemplosPresentaciones: [
        { nombre: "Unidad", factor: 1, base: true },
        { nombre: "Caja x12", factor: 12, base: false },
        { nombre: "Pack x3", factor: 3, base: false },
        { nombre: "500g", factor: 1, base: true },
        { nombre: "100g", factor: 0.2, base: false },
      ],
    };
  },
};
</script>

<style scoped lang="scss">
$accent: #3ea6ff;
$border: rgba(255, 255, 255, 0.07);
$bg: #0f0f0f;
$bg-panel: #161616;

.ayuda-wrapper {
  background-color: $bg;
  min-height: 100vh;
}

.ayuda-panel {
  background-color: $bg-panel !important;
  border: 1px solid $border !important;
  border-radius: 10px !important;
  &::before { box-shadow: none !important; }
}

.ayuda-header {
  font-size: 0.85rem !important;
  min-height: 52px !important;
}

.panel-title {
  font-size: 0.78rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: #ddd;
}

.ayuda-content {
  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 20px 20px;
  }
}

.faq-item {
  margin-bottom: 20px;
  &:last-child { margin-bottom: 0; }
}

.faq-q {
  font-size: 0.78rem;
  font-weight: 800;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
}

.help-text {
  font-size: 0.85rem;
  color: #888;
  line-height: 1.7;
  margin-bottom: 8px;
  em { color: #bbb; font-style: normal; font-weight: 600; }
  strong { color: #bbb; }
}

.help-list {
  margin: 4px 0 0 16px;
  li {
    font-size: 0.83rem;
    color: #888;
    line-height: 1.7;
    strong { color: #bbb; }
  }
}

.tipo-stock-bloque {
  background: rgba(255,255,255,0.02);
  border: 1px solid $border;
  border-radius: 8px;
  padding: 14px 16px;
}

.tipo-stock-nombre {
  font-size: 0.78rem;
  font-weight: 900;
  color: $accent;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 6px;
}

.ejemplo-inline {
  font-size: 0.78rem;
  color: #555;
  font-style: italic;
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
}

.ayuda-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.83rem;

  th {
    text-align: left;
    font-size: 0.68rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: #555;
    padding: 8px 12px;
    border-bottom: 1px solid $border;
  }
  td {
    padding: 9px 12px;
    color: #888;
    border-bottom: 1px solid rgba(255,255,255,0.03);
  }
  .factor-cell {
    font-family: 'JetBrains Mono', monospace;
    color: $accent;
    font-weight: 700;
  }
  tr:last-child td { border-bottom: none; }
}
</style>
